import {
  extractProductGroups,
  SectionBasedProduct,
} from "@brandclub/common-ui";
import { marked } from "marked";
import React from "react";
import { ProductGroupCarousel } from "../ProductGroupCarousel";
import { FeaturedProductWidget } from "../components/FeaturedProductWidget";
import { processProductGroupTags } from "./productGroupParser";

// Configure marked for proper HTML rendering
marked.use({
  gfm: true,
  breaks: true,
  pedantic: false,
});

/**
 * Render message content with product group carousels and featured products embedded
 */
export const renderMessageContentWithCarousels = (
  text: string,
  productMetadataMap: Map<string, SectionBasedProduct>,
  streamingGroups: Map<number, string[]>,
  isStreaming: boolean = false,
  isAlreadyProcessed: boolean = false
): React.ReactNode[] => {
  // Extract featured product tags and replace with placeholders BEFORE processing
  // This preserves their positions in the message
  // Handle both self-closing tags (/>) and tags with optional closing slash
  // Also handle flexible whitespace (including newlines) around attributes
  const featuredProductRegex = /<product\s+featured\s*=\s*"true"\s+stacklineSku\s*=\s*"([^"]+)"(?:\s+question\s*=\s*"([^"]+)")?\s*\/?>/g;
  const featuredProducts: Array<{
    placeholder: string;
    stacklineSku: string;
    question: string;
  }> = [];
  
  let featuredProductIndex = 0;
  let textWithFeaturedProductPlaceholders = text.replace(
    featuredProductRegex,
    (match, stacklineSku, question) => {
      // Use a placeholder format that markdown won't interpret
      // Using @@ which markdown doesn't interpret
      const placeholder = `@@FEATURED_PRODUCT_${featuredProductIndex}@@`;
      featuredProducts.push({
        placeholder,
        stacklineSku,
        question: question || "",
      });
      featuredProductIndex++;
      return placeholder;
    }
  );

  // Process product group tags BEFORE markdown parsing (if not already processed)
  // For streaming messages, text is already processed with |||PRODUCT_GROUP_N||| placeholders
  // For loaded messages, we need to process the raw <product_group> tags first
  const processedText = isAlreadyProcessed
    ? textWithFeaturedProductPlaceholders
    : processProductGroupTags(textWithFeaturedProductPlaceholders);

  // Now parse markdown
  const htmlContent = marked.parse(processedText) as string;

  // Remove <hr> elements from the parsed HTML
  const htmlContentWithoutHr = htmlContent.replace(/<hr\s*\/?>/gi, "");

  // Split by both product group placeholders AND featured product placeholders
  // NOTE: pipes must be escaped in the regex, otherwise the split matches empty strings
  const parts = htmlContentWithoutHr.split(
    /(\|\|\|PRODUCT_GROUP_\d+\|\|\||@@FEATURED_PRODUCT_\d+@@)/
  );

  // Extract product groups from the original text to get stacklineSku references
  const productGroups = extractProductGroups(text);

  return parts.map((part, idx) => {
    // Check if this part is a product group placeholder
    const groupMatch = part.match(/\|\|\|PRODUCT_GROUP_(\d+)\|\|\|/);
    if (groupMatch) {
      const groupIndex = parseInt(groupMatch[1]);

      // For streaming: use streamingGroups if available, otherwise extract from text
      // For loaded: always extract from text
      let allStacklineSkus: string[];
      if (isStreaming && streamingGroups.has(groupIndex)) {
        allStacklineSkus = streamingGroups.get(groupIndex) || [];
      } else {
        const group = productGroups[groupIndex];
        allStacklineSkus = group?.products.map((p) => p.stacklineSku) || [];
      }

      // Map stacklineSkus to actual product metadata from the centralized map
      const groupProductsWithMetadata = allStacklineSkus
        .map((sku) => productMetadataMap.get(sku))
        .filter(Boolean) as SectionBasedProduct[];

      return (
        <ProductGroupCarousel
          key={`group-${idx}`}
          products={groupProductsWithMetadata}
          allStacklineSkus={allStacklineSkus}
          isLoading={isStreaming && allStacklineSkus.length === 0}
        />
      );
    }

    // Check if this part is a featured product placeholder
    const featuredMatch = part.match(/@@FEATURED_PRODUCT_(\d+)@@/);
    if (featuredMatch) {
      const featuredIndex = parseInt(featuredMatch[1]);
      const featuredProduct = featuredProducts[featuredIndex];
      
      if (featuredProduct) {
        const product = productMetadataMap.get(featuredProduct.stacklineSku);
        if (product) {
          return (
            <FeaturedProductWidget
              key={`featured-${idx}-${featuredProduct.stacklineSku}`}
              product={product}
            />
          );
        } else {
          // Product metadata not available - this usually means the API didn't return product data for this SKU
          // Log a warning for debugging but don't render a widget
          if (process.env.NODE_ENV === 'development') {
            console.warn(
              `FeaturedProductWidget: Product metadata not found for SKU: ${featuredProduct.stacklineSku}. ` +
              `Available SKUs: ${Array.from(productMetadataMap.keys()).join(', ')}`
            );
          }
        }
      }
      // If product not found, return empty fragment
      return <React.Fragment key={`featured-missing-${idx}`} />;
    }

    // Regular content - use dangerouslySetInnerHTML to render HTML
    // Skip empty parts (they might be empty strings from splitting)
    if (!part.trim()) {
      return <React.Fragment key={`empty-${idx}`} />;
    }

    return (
      <div
        key={idx}
        style={{ display: "flex", flexDirection: "column" }}
        dangerouslySetInnerHTML={{ __html: part }}
        className="message-markdown"
      />
    );
  });
};
